import { Link } from "react-router-dom";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { dateFnsLocaleFor } from "@/lib/appLocale";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

export interface RecentActivityItem {
  id: string;
  completed_at: string;
  staff_name: string | null;
  staff_id: string | null;
  location_address: string;
  location_id: string;
  /** Nazwa konkretnej czynności/kroku (np. "Odkurzanie salonu") */
  step_name: string;
}

interface AdminDashboardActivityProps {
  items: RecentActivityItem[];
  hasMore?: boolean;
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
  className?: string;
}

export function AdminDashboardActivity({
  items,
  hasMore,
  onLoadMore,
  isLoadingMore,
  className,
}: AdminDashboardActivityProps) {
  const { t, i18n } = useTranslation();
  const dateLocale = dateFnsLocaleFor(i18n.language);

  if (items.length === 0) {
    return (
      <div
        className={cn(
          "rounded-lg border border-border bg-card p-4 text-center text-sm text-muted-foreground",
          className
        )}
      >
        {t("admin.activity.none")}
      </div>
    );
  }

  return (
    <div className={cn("rounded-lg border border-border bg-card p-4", className)}>
      <h3 className="text-sm font-semibold mb-3">{t("admin.activity.title")}</h3>
      <ul className="space-y-2">
        {items.map((a) => (
          <li
            key={a.id}
            className="rounded border border-border bg-muted/30 p-2 text-sm"
          >
            <div className="flex flex-wrap gap-x-2 gap-y-0.5">
              {a.staff_id ? (
                <Link
                  to={`/workers/${a.staff_id}`}
                  className="font-medium text-primary hover:underline"
                >
                  {a.staff_name || t("admin.activity.unknown")}
                </Link>
              ) : (
                <span className="font-medium">{a.staff_name || t("admin.activity.unknown")}</span>
              )}
              <span className="text-muted-foreground">{t("admin.activity.completed")}</span>
              <span>{a.step_name}</span>
              <span className="text-muted-foreground">{t("admin.activity.at")}</span>
              {a.location_id ? (
                <Link
                  to={`/properties/${a.location_id}`}
                  className="text-primary hover:underline"
                >
                  {a.location_address}
                </Link>
              ) : (
                <span>{a.location_address}</span>
              )}
            </div>
            <div className="text-muted-foreground text-xs mt-0.5">
              {format(new Date(a.completed_at), "dd MMM yyyy, HH:mm", { locale: dateLocale })}
            </div>
          </li>
        ))}
      </ul>
      {hasMore && onLoadMore && (
        <div className="flex justify-center pt-3">
          <Button
            variant="outline"
            size="sm"
            onClick={onLoadMore}
            disabled={isLoadingMore}
          >
            {isLoadingMore ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : null}
            {t("admin.activity.loadOlder")}
          </Button>
        </div>
      )}
    </div>
  );
}
